import React, { useEffect, useState } from "react";
import { Button, Container, Form, InputGroup } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import { useHistory } from "react-router-dom";
import { addSeniorRequest } from "../../redux/actions/senior";
import { AppState } from "../../redux/types";
import { formatRegionOption, REGIONS, ROLE } from "../../utils/constants";
import Loader from "../Loader";
import Message from "../Message";
import styles from "./registerSeniorForm.module.css";

const RegisterSeniorForm = () => {
    const history = useHistory();

    const regionCode = useSelector((state: AppState) => state.region?.code ?? "FI");
    const checkoutRegion = sessionStorage.getItem("confirmedBuyRegion");
    const lockedRegionCode = checkoutRegion || regionCode;
    const countryOptions = checkoutRegion ? REGIONS.filter((r) => r.code === checkoutRegion) : REGIONS;

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        username: "",
        password: "",
        seniorHomeId: "",
        country: lockedRegionCode,
    });

    const [validationMessage, setValidationMessage] = useState("");
    const [btnDisabled, setBtnDisabled] = useState(true);
    const [showPass, setShowPass] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const dispatch = useDispatch();
    const user = useSelector((state: AppState) => state.user);
    const seniorState = useSelector((state: AppState) => state.senior);
    const seniorHomes = useSelector((state: AppState) => state.seniorHomes.list);
    const { loading, error } = seniorState;
    const showPassHandler = () => {
        setShowPass((current) => !current);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: typeof value === "string" ? value.trim() : value,
        }));
    };

    const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSubmitted(true);

        dispatch(addSeniorRequest(formData, history));
    };

    useEffect(() => {
        const { firstName, lastName, username, password, seniorHomeId } = formData;
        let msg = "";
        let disabled = true;

        if (!firstName.trim() || !lastName.trim() || !username.trim() || !password.trim() || !seniorHomeId) {
            disabled = true;
        } else if (password.trim().length > 0 && password.trim().length < 11) {
            msg = "Salasana on liian lyhyt"; // Password is too short
            disabled = true;
        } else {
            disabled = false;
        }

        setValidationMessage(msg);
        setBtnDisabled(disabled);
    }, [formData]);

    useEffect(() => {
        if (submitted && !loading && !error) {
            setFormData({ firstName: "", lastName: "", username: "", password: "", seniorHomeId: "", country: lockedRegionCode });
            setSubmitted(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, error, submitted]);

    useEffect(() => {
        if (user.role === ROLE.HEAD_NURSE && seniorHomes.length > 0) {
            setFormData((prevFormData) => ({
                ...prevFormData,
                seniorHomeId: String(seniorHomes[0].id),
            }));
        }
    }, [seniorHomes, user.role]);

    const isHeadNurse = user.role === ROLE.HEAD_NURSE;

    return (
        <Container className={styles.seniorFormContainer}>
            <div className={styles.seniorFormSubContainer}>
                <h3 className="user-form-header text-center mb-4">Luo tili seniorille</h3> {/* Create account for senior */}
                {loading && <Loader />}
                {validationMessage && <Message variant="danger">{validationMessage}</Message>}
                <Form onSubmit={submitHandler} noValidate className={styles.seniorForm}>
                    <Form.Group controlId="firstName">
                        <Form.Label className={styles.label}>Etunimi</Form.Label>
                        <Form.Control type="text" placeholder="Etunimi" name="firstName" value={formData.firstName} onChange={handleChange} required />
                    </Form.Group>

                    <Form.Group controlId="lastName">
                        <Form.Label className={styles.label}>Sukunimi</Form.Label>
                        <Form.Control type="text" placeholder="Sukunimi" name="lastName" value={formData.lastName} onChange={handleChange} required />
                    </Form.Group>

                    <Form.Group controlId="username">
                        <Form.Label className={styles.label}>Käyttäjätunnus</Form.Label>
                        <Form.Control type="text" placeholder="Käyttäjätunnus" name="username" value={formData.username} onChange={handleChange} required />
                        <Form.Text className="text-muted">Tunnus voi olla sähköpostiosoite, mutta sen ei tarvitse olla.</Form.Text>
                    </Form.Group>

                    <Form.Group controlId="password">
                        <Form.Label className={styles.label}>Salasana</Form.Label>
                        <InputGroup>
                            <Form.Control type={showPass ? "text" : "password"} placeholder="Salasana" name="password" value={formData.password} onChange={handleChange} required />
                            <Button variant="outline-secondary" onClick={showPassHandler} className={`${styles.showPasswordBtn} shadow-none`}>
                                <i className={showPass ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                            </Button>
                        </InputGroup>
                        <small className="password-length">Vähintään 11 merkkiä. Näet salasanasi klikkaamalla silmäkuvaketta</small>
                    </Form.Group>

                    <Form.Group controlId="seniorHome">
                        <Form.Label className={styles.label}>Yritys</Form.Label>
                        <Form.Control as="select" name="seniorHomeId" onChange={handleChange} value={String(formData.seniorHomeId)} disabled={isHeadNurse} defaultValue={isHeadNurse ? seniorHomes?.[0]?.id : ""}>
                            <option value="">{isHeadNurse ? seniorHomes?.[0]?.name : "Valitse Yritys"}</option>
                            {(seniorHomes ?? []).map((seniorHome) => {
                                return (
                                    <option value={String(seniorHome.id)} key={String(seniorHome.id)}>
                                        {seniorHome.name}
                                    </option>
                                );
                            })}
                        </Form.Control>
                    </Form.Group>

                    <Form.Group controlId="country">
                        <Form.Label className={styles.label}>Maa / Country</Form.Label>
                        <Form.Control as="select" name="country" value={formData.country} onChange={handleChange} disabled={!!checkoutRegion}>
                            {countryOptions.map((r) => (
                                <option key={r.code} value={r.code}>
                                    {formatRegionOption(r)}
                                </option>
                            ))}
                        </Form.Control>
                    </Form.Group>

                    <Button type="submit" className={`${styles.seniorSubmitButton} tallenna mt-4`} disabled={btnDisabled || loading}>
                        {loading ? "Tallennetaan..." : "Tallenna"}
                    </Button>
                </Form>
            </div>
        </Container>
    );
};

export default RegisterSeniorForm;
